import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, X, Sparkles, Calendar, Crown, Lock, Wallet, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const DailyBonusPopup = ({ show, onClose, bonusData, onCashIn }) => {
    // Show if (a) bonus was claimed OR (b) bonus was blocked (needs $20 load)
    const claimed = bonusData?.daily_bonus_claimed;
    const blocked = bonusData?.daily_bonus_blocked;
    if (!show || (!claimed && !blocked)) return null;
    const minLoad = bonusData?.daily_bonus_min_load || 20;

    return (
        <AnimatePresence>
            {show && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ scale: 0.5, y: 50 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.5, y: 50 }}
                        className="card-dark p-8 max-w-md w-full text-center relative overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                        style={{ border: '2px solid #FFD700' }}
                    >
                        {/* Close button */}
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 text-[#A0AAB2] hover:text-white transition-colors"
                        >
                            <X className="w-6 h-6" />
                        </button>

                        {/* Background sparkles */}
                        <div className="absolute inset-0 pointer-events-none">
                            {[...Array(20)].map((_, i) => (
                                <motion.div
                                    key={i}
                                    className="absolute"
                                    style={{
                                        left: `${Math.random() * 100}%`,
                                        top: `${Math.random() * 100}%`,
                                    }}
                                    initial={{ opacity: 0, scale: 0 }}
                                    animate={{ 
                                        opacity: [0, 1, 0],
                                        scale: [0, 1, 0],
                                        rotate: [0, 180]
                                    }}
                                    transition={{
                                        duration: 2,
                                        delay: i * 0.1,
                                        repeat: Infinity
                                    }}
                                >
                                    <Sparkles className="w-4 h-4 text-[#FFD700]/50" />
                                </motion.div>
                            ))}
                        </div>

                        {claimed ? (
                            /* ---- CLAIMED state ---- */
                            <>
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ delay: 0.2, type: "spring", bounce: 0.5 }}
                                    className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#FFD700] to-[#FF8C00] flex items-center justify-center"
                                >
                                    <Gift className="w-10 h-10 text-black" />
                                </motion.div>

                                <h2 className="text-2xl font-black text-white mb-2" data-testid="daily-bonus-claimed-title">Daily Free Play!</h2>
                                
                                {bonusData?.login_streak > 1 && (
                                    <div className="flex items-center justify-center gap-2 mb-4">
                                        <Calendar className="w-4 h-4 text-[#FFD700]" />
                                        <span className="text-[#FFD700] font-bold">
                                            Day {bonusData.login_streak} Streak!
                                        </span>
                                    </div>
                                )}

                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ delay: 0.4, type: "spring", bounce: 0.6 }}
                                    className="mb-6"
                                >
                                    <p className="text-5xl font-mono font-black text-[#FFD700]" style={{ textShadow: '0 0 20px rgba(255, 215, 0, 0.5)' }}>
                                        +{bonusData?.daily_bonus_amount?.toFixed(0)}
                                    </p>
                                    <p className="text-[#A0AAB2] mt-1">free play credits — play anywhere, not cashable</p>
                                </motion.div>

                                {bonusData?.vip_name && bonusData?.vip_name !== 'Bronze' && (
                                    <div className="flex items-center justify-center gap-2 mb-4 p-2 rounded-lg bg-white/5">
                                        <Crown className="w-4 h-4" style={{ color: bonusData?.vip_color }} />
                                        <span className="text-sm" style={{ color: bonusData?.vip_color }}>
                                            {bonusData.vip_name} VIP Bonus Applied!
                                        </span>
                                    </div>
                                )}

                                <Button
                                    onClick={onClose}
                                    className="w-full btn-primary py-4 rounded-xl font-bold"
                                    data-testid="daily-bonus-claimed-continue"
                                >
                                    Awesome!
                                </Button>

                                <p className="text-[#A0AAB2] text-xs mt-4">
                                    Load ${minLoad}+ tomorrow to keep your streak going!
                                </p>
                            </>
                        ) : (
                            /* ---- BLOCKED state: needs $20+ load ---- */
                            <>
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ delay: 0.2, type: "spring", bounce: 0.5 }}
                                    className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center"
                                    style={{
                                        background: 'linear-gradient(135deg,#A855F7,#FF4DB8)',
                                        boxShadow: '0 0 30px rgba(168,85,247,0.5)',
                                    }}
                                >
                                    <Lock className="w-10 h-10 text-white" />
                                </motion.div>

                                <h2 className="text-2xl font-black text-white mb-2" data-testid="daily-bonus-blocked-title">
                                    Daily Free Play Locked
                                </h2>
                                <p className="text-[#E0BBFF] text-sm mb-5">
                                    Load <b className="text-[#FFD700]">${minLoad}+</b> in a single credit load today to unlock your daily free play bonus 🎁
                                </p>

                                <div className="rounded-lg p-3 mb-5"
                                     style={{
                                         background: 'rgba(255,215,0,0.07)',
                                         border: '1px dashed rgba(255,215,0,0.35)',
                                     }}>
                                    <p className="text-[11px] text-[#FFD700] font-bold mb-1">What you'll get</p>
                                    <ul className="text-[11px] text-[#E0E0E0] leading-relaxed text-left pl-4 list-disc">
                                        <li>Daily free play credits (playable on any game)</li>
                                        <li>Build streak multipliers (up to 7 days)</li>
                                        <li>VIP-boosted payouts</li>
                                    </ul>
                                </div>

                                <Button
                                    onClick={onCashIn}
                                    className="w-full py-4 rounded-xl font-black text-base"
                                    style={{
                                        background: 'linear-gradient(135deg,#FFD700,#FF8C00)',
                                        color: '#1a0030',
                                        boxShadow: '0 0 20px rgba(255,215,0,0.4)',
                                    }}
                                    data-testid="daily-bonus-cashin-btn"
                                >
                                    <Wallet className="w-5 h-5 mr-2 inline" />
                                    Load ${minLoad}+ to Unlock
                                </Button>

                                <button
                                    onClick={onClose}
                                    className="w-full mt-3 flex items-center justify-center gap-2 py-3 rounded-2xl font-black text-[13px] tracking-wide transition-colors"
                                    style={{
                                        background: 'linear-gradient(180deg, #1a0030, #0a0018)',
                                        border: '1.5px solid #FFD70055',
                                        color: '#FFD700',
                                        boxShadow: '0 4px 20px rgba(255,215,0,0.15)',
                                    }}
                                    data-testid="daily-bonus-return-to-game"
                                >
                                    <ArrowLeft className="w-4 h-4" strokeWidth={2.8} />
                                    Return to game
                                </button>
                            </>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default DailyBonusPopup;
